import { useEffect, useState } from "react";
import { useSession, useResendVerification } from "@/lib/queries";
import { useToast } from "@/components/ui/Toast";

interface SessionShape {
  user?: { emailVerified?: boolean; email?: string };
}

const DISMISS_KEY = "dw.email-verify-banner.dismissed-until";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

// Belt-and-suspenders persistence: localStorage is primary, sessionStorage
// is the fallback when localStorage is blocked (incognito tightening, some
// privacy-tooling setups, embedded webviews). An in-memory cache is the
// last resort so dismissal at least survives within the current SPA
// lifetime even if both storages are unavailable.
let memoryDismissedUntil = 0;

function readDismissedUntil(): number {
  let best = memoryDismissedUntil;
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      const raw = store?.getItem(DISMISS_KEY);
      if (!raw) continue;
      const parsed = Number.parseInt(raw, 10);
      if (Number.isFinite(parsed) && parsed > best) best = parsed;
    } catch {
      /* storage may be unavailable; check the next layer */
    }
  }
  return best;
}

function writeDismissedUntil(until: number): void {
  memoryDismissedUntil = until;
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      store?.setItem(DISMISS_KEY, String(until));
    } catch {
      /* one storage layer may be blocked; the others still apply */
    }
  }
}

export function EmailVerificationBanner() {
  const { data } = useSession();
  const session = (data ?? {}) as SessionShape;
  const verified = session.user?.emailVerified;

  const [dismissedUntil, setDismissedUntil] = useState<number>(() => readDismissedUntil());
  const resendMut = useResendVerification();
  const { toast } = useToast();

  useEffect(() => {
    if (verified !== false) return;
    const id = window.setInterval(() => setDismissedUntil(readDismissedUntil()), 60_000);
    return () => window.clearInterval(id);
  }, [verified]);

  if (verified !== false) return null;
  if (Date.now() < dismissedUntil) return null;

  function handleDismiss() {
    const until = Date.now() + DISMISS_TTL_MS;
    writeDismissedUntil(until);
    setDismissedUntil(until);
  }

  function handleResend() {
    resendMut.mutate(undefined, {
      onSuccess: (res) => {
        if (res.alreadyVerified) {
          toast({
            title: "Email already verified",
            description: "Refresh to clear this banner.",
            tone: "success",
          });
        } else {
          toast({
            title: "Verification email sent",
            description: "Check your inbox.",
            tone: "success",
          });
        }
      },
      onError: (err) =>
        toast({
          title: "Could not resend verification email",
          description: (err as Error).message,
          tone: "error",
        }),
    });
  }

  return (
    <div
      role="status"
      className="border-b border-dw-border bg-dw-surface px-dw-4 py-1 text-xs text-dw-text-2"
    >
      <div className="flex items-center gap-dw-3">
        <span aria-hidden="true" className="text-dw-orange">●</span>
        <span className="truncate">
          Verify your email to unlock all features. Check your inbox.
        </span>
        <div className="ml-auto flex items-center gap-dw-3">
          <button
            type="button"
            className="text-dw-blue hover:underline disabled:opacity-50"
            disabled={resendMut.isPending}
            onClick={handleResend}
          >
            {resendMut.isPending ? "Sending…" : "Resend"}
          </button>
          <button
            type="button"
            className="text-dw-text-3 hover:text-dw-text"
            onClick={handleDismiss}
            aria-label="Dismiss verification banner for 24 hours"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
